import { PolIcon } from "components/PolIcon/policon";
import React, { useMemo } from 'react';
import { Draggable } from "react-beautiful-dnd";
import { useWatch } from "react-hook-form";
import Field from "v4/components/form/Field/Field";
import QuoteLineDescription
    from "v4/features/front/products/components/QuotesProductsDetails/components/QuotesProductsTable/components/QuoteLineDescription/QuoteLineDescription";
import LineTotal
    from "v4/features/front/products/components/QuotesProductsDetails/components/QuotesProductsTable/components/QuotesProductsTableSection/components/LineRow/components/LineTotal/LineTotal";
import { usePolTranslation } from "v4/hooks/usePolTranslation";
import { generateUrlFromRelativeUrl } from "v4/services/api.service";
import { financial, formatNumberWithScale } from "v4/utils";

export default React.memo(function LineRow({
                                               field,
                                               index,
                                               realIndex,
                                               isShow,
                                               editAttributesFor,
                                               remove,
                                               properties,
                                               formMetadata,
                                           }) {
    const { t } = usePolTranslation();

    const fieldKeys = Object.keys(field).join('|');
    const names = useMemo(() => fieldKeys.split('|').reduce((prev, current) => (
        {
            ...prev,
            [current]: `quoteLines.${realIndex}.${current}`
        }
    ), {
        vat: `quoteLines.${realIndex}.vatRate`,
        comment: `quoteLines.${realIndex}.comment`,
        description: `quoteLines.${realIndex}.description`,
    }), [realIndex, fieldKeys]);

    const [discountUnitPrice, discountQuantity, discountDiscountType] = useWatch({
        name: [
            names.unitPriceExclVat,
            names.quantity,
            names.discountType,
        ],
    });
    const discountValueConstraints = {
        constraints: {
            ...properties.discountValue?.constraints,
            LessThanOrEqual: {
                value: getMaxDiscountPossible([discountUnitPrice, discountQuantity, discountDiscountType]),
            },
        }
    };

    const product = field?.quoteLineInfo?.product;

    return (
        <Draggable key={field.id}
                   draggableId={field.id}
                   index={index}
                   isDragDisabled={isShow}>
            {(provided) => (
                <div className="quotes-products__item"
                     ref={provided.innerRef}
                     {...provided.draggableProps}
                     {...provided.dragHandleProps}>
                    <div className="quotes-products__item__img">
                        {
                            product?.imageUrl &&
                            <img
                                src={generateUrlFromRelativeUrl(product.imageUrl)}
                                alt={field.name}/>
                        }
                    </div>
                    <div
                        className="quotes-products__item__optional" data-label={t('optional')}>
                        {
                            isShow
                                ?
                                <p className="quotes-products__item__label__is_optional">{t(field.isOptional ? 'yes' : 'no')}</p>
                                :
                                <Field {...properties.isOptional} name={names.isOptional}/>
                        }
                    </div>
                    <div
                        className="quotes-products__item__label" data-label={t('designation')}>
                        <div>
                            {
                                product?.id || isShow
                                    ? (
                                        <div
                                            className="quotes-products__item__product">
                                            <div className="d-flex align-items-center justify-content-between mb-1"
                                                 style={{ gap: '0.4rem', minHeight: '2.3rem' }}>
                                                <p className="mb-0">{field.name}</p>
                                                <QuoteLineDescription isShow={isShow} name={names.description}
                                                                      field={field} formMetadata={formMetadata}/>
                                            </div>
                                            {product?.reference &&
                                                <p>{t('ref')} : {product.reference}</p>}
                                            {product?.categories &&
                                                <p>
                                                    {t(product.categories.length > 1 ? 'productFamilies' : 'productFamily')} : {product.categories.map(({ name }) => name).join(', ')}
                                                </p>}
                                        </div>
                                    )
                                    : (
                                        <div className="d-flex align-items-center justify-content-between"
                                             style={{ gap: '0.4rem' }}>
                                            <div
                                                className="input-field-wrapper input-field-wrapper--label mb-0">
                                                <Field {...properties.name} name={names.name}
                                                       placeholder={t('product_label')}/>
                                                <PolIcon icon="edit"/>
                                            </div>
                                            <QuoteLineDescription isShow={isShow}
                                                                  name={names.description}
                                                                  field={field} formMetadata={formMetadata}/>
                                        </div>
                                    )
                            }
                            {
                                isShow
                                    ?
                                    <p className="quotes-products__item__label__commentary">{field.comment}</p>
                                    : <Field placeholder={t('comment')}
                                             {...properties.comment}
                                             name={names.comment}/>
                            }
                        </div>
                    </div>
                    <div
                        className="quotes-products__item__price" data-label={t('quotes_unit_price')}>
                        {
                            isShow
                                ?
                                <p>{financial(field.unitPriceExclVat, true, getScale(properties.unitPriceExclVat))}</p>
                                : <div
                                    className='input-field-wrapper input-field-wrapper--euro'>
                                    <Field {...properties.unitPriceExclVat}
                                           name={names.unitPriceExclVat}/>
                                    <PolIcon icon="euro"/>
                                </div>
                        }
                    </div>
                    <div
                        className="quotes-products__item__quantity" data-label={t('quantity')}>
                        {
                            isShow
                                ? <p>{formatNumberWithScale(field.quantity, getScale(properties.quantity))}</p>
                                : <div
                                    className='input-field-wrapper'>
                                    <Field {...properties.quantity} name={names.quantity}/>
                                </div>
                        }
                    </div>
                    <div
                        className="quotes-products__item__discount" data-label={t('discount')}>
                        <div>
                            {
                                isShow
                                    ? (
                                        <p>
                                            {
                                                field.discountValue > 0
                                                    ? (
                                                        field.discountType === 'amount'
                                                            ? financial(field.discountValue, true, getScale(properties.discountValue))
                                                            : `${formatNumberWithScale(field.discountValue, getScale(properties.discountValue))} %`
                                                    )
                                                    : '-'
                                            }
                                        </p>
                                    )
                                    : (
                                        <>
                                            <div
                                                className='input-field-wrapper'>
                                                <Field {...properties.discountValue}
                                                       name={names.discountValue}
                                                       {...discountValueConstraints}/>
                                            </div>
                                            <Field specificParams={{ isClearable: false }}
                                                   {...properties.discountType}
                                                   name={names.discountType}
                                            />
                                        </>
                                    )
                            }
                        </div>
                    </div>
                    <div className="quotes-products__item__vat" data-label={t('vat')}>
                        {isShow
                            ? (
                                <p>{(properties.vatRate?.choices ?? []).find(choice => choice?.value === field.vatRate)?.label}</p>
                            ) : (
                                <Field specificParams={{ isClearable: false }}
                                       {...properties.vatRate}
                                       name={names.vat}/>
                            )
                        }
                    </div>
                    <div
                        className="quotes-products__item__total" data-label={t('excl_taxes_amount')}>
                        <div>
                            <LineTotal names={names}/>
                        </div>
                    </div>
                    <div
                        className="quotes-products__item__actions">
                        <div>
                            <div
                                className="quotes-products__item__actions__btns">
                                {!isShow && (
                                    <button type="button"
                                            onClick={() => remove(realIndex)}>
                                        <PolIcon icon="trash"/>
                                    </button>
                                )}
                                {!!product?.productConfiguration && (
                                    <button type="button"
                                            onClick={() => editAttributesFor(realIndex)}>
                                        <PolIcon icon="cog"/>
                                    </button>
                                )}
                            </div>
                            {!isShow && <PolIcon icon="draggable"/>}
                        </div>
                    </div>
                </div>
            )}
        </Draggable>
    );
});

function getScale(property) {
    return property?.attr?.['use-scale'] ? property?.scale ?? null : null;
}

function getMaxDiscountPossible([unit_price = 0, quantity = 0, discount_type = 'amount']) {
    return discount_type === 'amount' ? (unit_price * quantity) : 100;
}
